import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { useEffect, useState } from "react";
import { css } from "@emotion/react";
import { UserSquare } from "@hl/base-components/lib/assets/icons/HDS";
import { AnnotationQuestion } from "@hl/base-components/lib/assets/icons.generated/HDS/Duotone icons/Communication/";
import { UserColorSchemeSettings, } from "@hl/base-components/lib/theme/ColorSchemeContext";
import { SHADOW_COLOR } from "@hl/base-components/lib/theme/button";
import { STATUS_COLOR, TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { Box, Button, Flex, Group, Menu, SegmentedControl, Text, UnstyledButton, useMantineTheme, } from "@mantine/core";
import { useInterval, useLocalStorage, useMediaQuery } from "@mantine/hooks";
import { IconChevronDown } from "@tabler/icons";
import { useFeeData, useAccount } from "wagmi";
import { Meter, MoonStar, SignOut, Sun } from "../../assets/icons.generated";
import { EXTERNAL_URL, STORAGE_KEYS, USER_ROUTES } from "../../config";
import { getDefaultAvatarUrl } from "../../utils/profile";
import { Balance } from "../balance/Balance";
import { ResizedImage } from "../image";
import { useOwnerOffersQuery } from "../marketplace/queries/marketplace.graphql.generated";
import { LocalOnboardingStatus, useOnboardingLS, } from "../onboarding/useOnboardingLS";
import OperationList from "../tx-history/TxHistoryList";
import TxUpdater from "../tx-history/TxUpdater";
import { UserMenuTitle } from "./UserMenuTitle";
import { useTxHistory } from "./hooks/useTxHistory";
export const UserMenu = ({ displayName, avatarUrl, hideAvatar = false, walletAddress, ensName, hideDisconnect, handleDisconnectClick, showAccountSettings, colorScheme, colorSchemeContext, refetchQueriesOnMined, }) => {
    var _a, _b, _c, _d, _e, _f;
    const { onboardingStatus, setOnboardingStatus } = useOnboardingLS();
    const [lastFetched, setLastFetched] = useLocalStorage({
        key: `${STORAGE_KEYS.newUserOffers}${walletAddress}`,
        defaultValue: new Date().toISOString(),
        getInitialValueInEffect: false,
    });
    const theme = useMantineTheme();
    const account = useAccount();
    const [lastFetchedInitial] = useState(lastFetched);
    const feeData = useFeeData();
    const interval = useInterval(() => feeData.refetch(), 120000);
    const defaultImgPath = getDefaultAvatarUrl(walletAddress !== null && walletAddress !== void 0 ? walletAddress : undefined);
    useEffect(() => {
        interval.start();
        return interval.stop;
    }, []);
    const { data } = useOwnerOffersQuery({
        variables: {
            chainId: null,
            createdAfter: new Date(lastFetchedInitial),
            tokenId: null,
            tokenAddress: null,
            aboveFloor: null,
            tokenOrCollectionName: "",
            sortDirection: null,
            sortBy: null,
            after: null,
            first: 1,
            criteriaKind: null,
            minPrice: null,
            floorPriceDifference: null,
        },
        skip: !lastFetchedInitial,
    });
    useEffect(() => {
        if (data) {
            setLastFetched(new Date().toISOString());
        }
    }, [data]);
    const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm - 1}px)`);
    const newOfferCount = (_a = data === null || data === void 0 ? void 0 : data.getOwnerOffers.pageInfo.totalCount) !== null && _a !== void 0 ? _a : null;
    const { txs, pendingTxs, loading: loadingTx, mintTokenUrl, manageCollectionUrl, } = useTxHistory();
    const affirmativeColor = theme.fn.themeColor(STATUS_COLOR.AFFIRMATIVE);
    const wallet = ensName !== null && ensName !== void 0 ? ensName : walletAddress;
    const profileLink = newOfferCount
        ? USER_ROUTES.user.marketplace.receivedOffers.replace(":slug", wallet || "")
        : USER_ROUTES.user.profile.replace(":slug", wallet || "");
    return (_jsxs(Menu, { width: 340, transition: "pop-top-right", position: "bottom-end", radius: 14, styles: {
            dropdown: {
                maxHeight: "60vh",
                overflowY: "auto",
            },
        }, children: [_jsx(Menu.Target, { children: _jsx(UnstyledButton, { children: _jsx(Button, { component: "div", color: SHADOW_COLOR, rightIcon: !isMobile && _jsx(IconChevronDown, { size: 16 }), px: "sm", children: _jsx(UserMenuTitle, { avatarUrl: avatarUrl, hideAvatar: hideAvatar, displayName: displayName, wallet: wallet, pendingTxsCount: pendingTxs.length }) }) }) }), _jsxs(Menu.Dropdown, { style: {
                    border: 0,
                }, children: [_jsx(Menu.Item, { component: "a", href: profileLink, target: "_blank", icon: _jsx(ResizedImage, { src: avatarUrl || defaultImgPath, fit: "fill", radius: 999, height: 20, width: 20, sx: {
                                border: `0.5px solid ${theme.colors.divider[0]}`,
                                borderRadius: 999,
                            } }), children: _jsxs(Flex, { justify: "space-between", align: "center", children: ["Profile", !!newOfferCount && (_jsxs(Text, { color: theme.colors.successStatus[0], size: "xs", fw: WEIGHT_BOLD, bg: theme.colors.successWashStatus[0], py: 2, px: 10, sx: { borderRadius: 999 }, children: [newOfferCount, " new offer", newOfferCount === 1 ? "" : "s"] }))] }) }), onboardingStatus === LocalOnboardingStatus.SKIPPED && (_jsx(Menu.Item, { icon: _jsx(UserSquare, { height: 20, width: 20 }), onClick: () => {
                            setOnboardingStatus(LocalOnboardingStatus.NOT_STARTED);
                        }, children: "Continue Onboarding" })), !hideDisconnect && (_jsx(Menu.Item, { onClick: handleDisconnectClick, "aria-label": "logout", icon: _jsx(SignOut, {}), children: "Sign Out" })), _jsx(Menu.Divider, { mx: "sm", color: "divider.0", my: "xs" }), _jsx(Menu.Item, { component: "a", href: EXTERNAL_URL.SUPPORT, target: "_blank", icon: _jsx(AnnotationQuestion, {}), children: "Help Center & Documentation" }), _jsx(Menu.Divider, { mx: "sm", color: "divider.0", my: "xs" }), ((_b = account === null || account === void 0 ? void 0 : account.chain) === null || _b === void 0 ? void 0 : _b.name) && (_jsxs(Group, { position: "apart", px: 12, py: 10, children: [_jsx(Text, { size: "sm", children: "Network" }), _jsx(Text, { size: "sm", color: TEXT_COLOR.SECONDARY, children: (_c = account === null || account === void 0 ? void 0 : account.chain) === null || _c === void 0 ? void 0 : _c.name })] })), isMobile && _jsx(Balance, { small: true }), ((_d = feeData === null || feeData === void 0 ? void 0 : feeData.data) === null || _d === void 0 ? void 0 : _d.formatted.gasPrice) && (_jsxs(Group, { position: "apart", px: 12, py: 10, children: [_jsx(Text, { size: "sm", children: "Gas" }), _jsx(Meter, { css: getMeterStyles(Number(((_e = feeData === null || feeData === void 0 ? void 0 : feeData.data) === null || _e === void 0 ? void 0 : _e.gasPrice) || 0), Number(((_f = feeData === null || feeData === void 0 ? void 0 : feeData.data) === null || _f === void 0 ? void 0 : _f.maxFeePerGas) || 1), affirmativeColor) })] })), txs.length > 0 && !loadingTx && (_jsxs(_Fragment, { children: [txs.map((tx) => (_jsx(TxUpdater, { tx: tx, refetchQueriesOnMined: refetchQueriesOnMined }, tx.txHash))), _jsx(Menu.Divider, { mx: "sm", color: "divider.0", my: "xs" }), _jsx(Box, { px: 12, py: 6, children: _jsx(OperationList, { operations: txs, manageCollectionUrl: manageCollectionUrl, mintTokenUrl: mintTokenUrl }) })] })), showAccountSettings && (_jsxs(_Fragment, { children: [_jsx(Menu.Divider, { mx: "sm", color: "divider.0", my: "xs" }), _jsx(SegmentedControl, { mb: 6, w: "100%", value: colorScheme, styles: { control: { border: "none !important" } }, onChange: (value) => colorSchemeContext === null || colorSchemeContext === void 0 ? void 0 : colorSchemeContext.selectUserColorScheme(value), data: [
                                    // {
                                    //   label: (
                                    //     <Group align="center" spacing="sm" position="center">
                                    //       <Browser width={16} height={16} />
                                    //       <Text size="xs" color={TEXT_COLOR.PRIMARY}>
                                    //         System
                                    //       </Text>
                                    //     </Group>
                                    //   ),
                                    //   value: UserColorSchemeSettings.SYSTEM,
                                    // },
                                    {
                                        label: (_jsxs(Group, { align: "center", spacing: "sm", position: "center", children: [_jsx(Sun, { width: 16, height: 16 }), _jsx(Text, { size: "xs", color: TEXT_COLOR.PRIMARY, children: "Light" })] })),
                                        value: UserColorSchemeSettings.LIGHT,
                                    },
                                    {
                                        label: (_jsxs(Group, { align: "center", spacing: "sm", position: "center", children: [_jsx(MoonStar, { width: 16, height: 16 }), _jsx(Text, { size: "xs", color: TEXT_COLOR.PRIMARY, children: "Dark" })] })),
                                        value: UserColorSchemeSettings.DARK,
                                    },
                                ] })] }))] })] }));
};
const getMeterStyles = (gasPrice, maxFeePerGas, affirmativeColor) => {
    const max = 5;
    let meterIndex = Math.round((gasPrice * max) / maxFeePerGas) + 1;
    if (meterIndex > max)
        meterIndex = max;
    const styles = [];
    for (let i = 1; i <= meterIndex; i++) {
        styles.push(css `
      rect:nth-of-type(${i}) {
        fill-opacity: 1;
        fill: ${affirmativeColor};
      }
    `);
    }
    return styles;
};
